import type { AxiosError } from 'axios'

import type { ApiErrorItem } from './types'

/**
 * The shapes an error body arrives in — and there is no one of them: a string, a list, a list
 * of objects and two different maps all turn up. Read one structurally, never by reaching for
 * the shape you expect.
 */
type ErrorBody = {
  message?: unknown
  error?: unknown
  errors?: unknown
}

/**
 * The first sentence anywhere in one of those, whatever it was wrapped in.
 *
 * Read structurally rather than by guessing the shape: `body.error?.trim()` threw a
 * TypeError the moment an endpoint answered with a list instead of a string, which in a
 * table's error state meant a blank screen instead of a message about a blank screen.
 */
function firstSentence(value: unknown): string | undefined {
  if (typeof value === 'string') return value.trim() || undefined

  if (Array.isArray(value)) {
    for (const entry of value) {
      const found = firstSentence(entry)
      if (found) return found
    }

    return undefined
  }

  if (value && typeof value === 'object') {
    const record = value as Record<string, unknown>

    // `title` is the v2 envelope's word for it; `messages` sits beside the `fields` it
    // belongs to, and the field names are not something to show anyone.
    for (const key of ['title', 'messages'] as const) {
      if (key in record) {
        const found = firstSentence(record[key])
        if (found) return found
      }
    }

    for (const entry of Object.values(record)) {
      const found = firstSentence(entry)
      if (found) return found
    }
  }

  return undefined
}

/**
 * What the server said went wrong, for the places that show one toast rather than a
 * form. A refused action names its reason — "Not authorized to perform this action" for
 * a record that is not the publisher's own — and that sentence tells the user what to do
 * next, where "something went wrong" leaves them to guess.
 *
 * The fallback belongs to the caller: only it knows what the user was doing. It is also
 * what a body with nothing to say falls back to — a dropped connection, or a gateway's
 * HTML page, which is a string and not a message about this request.
 */
export function serverErrorMessage(error: unknown, fallback: string): string {
  const body = (error as AxiosError<ErrorBody> | undefined)?.response?.data

  if (!body || typeof body !== 'object') return fallback

  return (
    firstSentence(body.message) ??
    firstSentence(body.error) ??
    firstSentence(body.errors) ??
    fallback
  )
}

/**
 * The machine-readable reasons a refusal names, for a screen that acts on the reason rather
 * than on the words. Empty whenever the body carries none, so a caller must have an answer for
 * that too.
 */
export function serverErrorTypes(error: unknown): string[] {
  const errors = (error as AxiosError<ErrorBody> | undefined)?.response?.data
    ?.errors

  if (!Array.isArray(errors)) return []

  return errors
    .map((item) => (item as ApiErrorItem | null)?.type)
    .filter((type): type is string => typeof type === 'string')
}

export function isNotFoundError(error: unknown): boolean {
  return (error as AxiosError | undefined)?.response?.status === 404
}
